import {
  AUCTION_DATE_HORIZON_DAYS,
  SOLD_PROBE_PREFIX,
  SOLD_PROVIDER_WINDOW_START_DAYS_AGO,
  SOLD_WINDOW_END_DAYS_AGO,
  auctionDay,
  getAuctionDateWindow,
  isApibaraColor,
  isApibaraDamage,
  isApibaraDriveType,
  isApibaraFuelType,
  isApibaraTransmission,
  isKeysOption,
  isRunCondition,
  MAX_ODOMETER_MILES,
  type TKeysOption,
  type TRunCondition,
} from './auctionConfig';
import type { TLotFilters } from './lotFilters';
import { isPast } from './normalizeDate';

/**
 * Visitor filters → apibara query parameters (spec 007).
 *
 * AN ALLOWLIST, FOR THE SAME REASON AS THE OLD PROVIDER'S: this API also accepts
 * unrecognised parameters with HTTP 200 and returns unfiltered data. A typo here
 * would show visitors the whole market labelled as a filtered result.
 *
 * Nothing is mapped here that was not MEASURED to filter. Gate 1 on 2026-09-03
 * checked returned rows against an unfiltered baseline for each one — a filter
 * passes only if every row satisfies it and the distribution actually moved.
 *
 * `drive_type` was withdrawn on that basis and has been RESTORED (spec 008). The
 * zero-row result came from testing `AWD` against a Ford/Copart slice that has no
 * AWD cars — Copart labels those "4x4 w/Front Whl Drv". Against the unfiltered
 * inventory, `FWD` and `RWD` each return 20 of 20 matching rows.
 */

/** URL parameter names for the filters this provider adds. Polish, as the rest. */
export const APIBARA_FILTER_PARAMS = {
  odometerFrom: 'przebiegOd',
  odometerTo: 'przebiegDo',
  transmission: 'skrzynia',
  fuel: 'paliwo',
  color: 'kolor',
  damage: 'szkoda',
  driveType: 'naped',
  runCondition: 'stanTech',
  keys: 'kluczyki',
  cursor: 'kursor',
} as const;

export type TApibaraFilters = TLotFilters & {
  odometerFrom: number | null;
  odometerTo: number | null;
  transmission: string[];
  fuel: string[];
  color: string[];
  damage: string[];
  driveType: string[];
  runCondition: TRunCondition | null;
  keys: TKeysOption | null;
  /**
   * Opaque paging token. Carried verbatim and NEVER constructed or parsed — it
   * encodes a keyset (`{vehicles.ad, vehicles.id}`) whose boundaries shift as
   * inventory changes.
   */
  cursor: string | null;
  /**
   * Where the live window starts. INTERNAL — set by `getAuctionLotsPage`, never
   * parsed from the address. `today` is the first attempt; `tomorrow` is the
   * top-up when today's lanes have mostly run (2026-09-07: the listing used to
   * start at tomorrow unconditionally and hid auctions four hours away).
   */
  windowStart?: 'today' | 'tomorrow';
  /** INTERNAL — the `today` attempt walks no extra pages; `tomorrow` does. */
  topUp?: boolean;
  /**
   * INTERNAL — the sitemap probe (spec 016, R6). First page only: today's
   * window, then tomorrow's if today was short, no extra cursor pages. The
   * queries are built by the same code as a visitor's first page, so the probe
   * and the page share one cache entry — a probe warms the page and a visit
   * answers the probe. Attributed to `sitemap` in the ledger.
   */
  probe?: boolean;
  /**
   * INTERNAL — the next-page warm-up (spec 016). After a listing renders, its
   * "Następne" page is fetched behind it at the `prefetch` tier, so the click a
   * visitor is most likely to make is instant. One level only.
   */
  prefetch?: boolean;
  /** True for a crawler's render: no next-page warm-up is started behind it. */
  noWarm?: boolean;
  /**
   * Prefix search on the model (spec 011), sent as `q`.
   *
   * When set, `model` is NOT sent. Measured: `make=BMW&model=X5&q=X5` returns
   * twenty rows all named exactly `X5` — the exact filter wins and silently
   * discards everything the prefix was added to find. That failure produces a
   * page identical to the bug being fixed, which is why the two are mutually
   * exclusive in `toApibaraParams` rather than by caller discipline.
   *
   * Never set for a Canadian (facility) query: `q` with `facility_id` returns
   * zero rows.
   */
  modelPrefix: string | null;
};

const parseMiles = (value: string | undefined): number | null => {
  if (!value) return null;

  const miles = Number(value);

  return Number.isFinite(miles) && miles >= 0 && miles <= MAX_ODOMETER_MILES
    ? Math.round(miles)
    : null;
};

/**
 * Cursors are opaque, so they cannot be validated for meaning — only for shape.
 * Anything that is not plausible base64url is dropped and the visitor gets the
 * first page rather than an error (FR-020).
 */
const parseCursor = (value: string | undefined): string | null => {
  if (!value) return null;

  const trimmed = value.trim();

  return /^[A-Za-z0-9_=-]{8,4096}$/.test(trimmed) ? trimmed : null;
};

export const parseApibaraExtras = (
  searchParams: Record<string, string | string[] | undefined>
): Omit<TApibaraFilters, keyof TLotFilters> => {
  const single = (key: string): string | undefined => {
    const value = searchParams[key];

    return Array.isArray(value) ? value[0] : value;
  };

  const many = (key: string, allow: (v: string) => boolean): string[] => {
    const value = searchParams[key];
    const list = Array.isArray(value) ? value : value ? [value] : [];

    // Deduplicated: a repeated value must not repeat the parameter upstream.
    return [...new Set(list.filter(allow))];
  };

  let odometerFrom = parseMiles(single(APIBARA_FILTER_PARAMS.odometerFrom));
  let odometerTo = parseMiles(single(APIBARA_FILTER_PARAMS.odometerTo));

  // An inverted range is a user error, not a provider one — normalise rather
  // than return a guaranteed-empty page.
  if (odometerFrom !== null && odometerTo !== null && odometerFrom > odometerTo) {
    [odometerFrom, odometerTo] = [odometerTo, odometerFrom];
  }

  const rawRun = single(APIBARA_FILTER_PARAMS.runCondition);
  const rawKeys = single(APIBARA_FILTER_PARAMS.keys);

  return {
    odometerFrom,
    odometerTo,
    transmission: many(APIBARA_FILTER_PARAMS.transmission, isApibaraTransmission),
    fuel: many(APIBARA_FILTER_PARAMS.fuel, isApibaraFuelType),
    color: many(APIBARA_FILTER_PARAMS.color, isApibaraColor),
    damage: many(APIBARA_FILTER_PARAMS.damage, isApibaraDamage),
    driveType: many(APIBARA_FILTER_PARAMS.driveType, isApibaraDriveType),
    runCondition: rawRun && isRunCondition(rawRun) ? rawRun : null,
    keys: rawKeys && isKeysOption(rawKeys) ? rawKeys : null,
    cursor: parseCursor(single(APIBARA_FILTER_PARAMS.cursor)),
    /*
     * NOT parsed from the URL, deliberately.
     *
     * A prefix is derived from the reviewed index for the model the route
     * resolved — it is never something a visitor supplies. Reading it from a
     * query string would let anyone hand the provider an arbitrary `q`, which
     * is both an unfiltered-results risk and a way to provoke the HTTP 502
     * described in `toApibaraParams`.
     */
    modelPrefix: null,
  };
};

export type TApibaraQuery = Record<string, string | number | string[] | undefined>;

/**
 * Builds the provider query.
 *
 * @param facility one yard AND the platform it belongs to, or `null` for no
 *   location filter.
 *
 *   Selecting Canada means selecting yards, since `loc_state=ON` returns HTTP
 *   500 and `office_name` returns nothing. And `facility_id` is **single-valued**
 *   — measured: repeating it keeps only the LAST value, a comma-separated list
 *   keeps only the FIRST, and the `facility_id[]` array form returns HTTP 500.
 *   So a multi-yard selection is a fan-out by the caller, not a parameter here.
 *
 *   The PLATFORM travels with the id because `facility_id` is unique per
 *   platform, not globally. Asking for "203" alone is an ambiguous question, and
 *   the answer may be the other platform's yard 203 in a different country.
 */
export const toApibaraParams = (
  filters: TApibaraFilters,
  facility: { facilityId: number; platform: string } | null,
  perPage: number
): TApibaraQuery => {
  const params: TApibaraQuery = { per_page: perPage };

  if (filters.cursor) params.cursor = filters.cursor;
  if (filters.make) params.make = filters.make;

  /*
   * MODEL: either an exact name or a prefix, NEVER both (spec 011).
   *
   * `q` is the vendor's own undocumented prefix parameter — the one their demo
   * uses and we never adopted. It is honoured: the control `q=ZZZQQQ` returns
   * nothing, which is what separates it from the five parameters on this API
   * that are accepted and silently ignored. One `q=X5` reaches `X5`, `X5 M` and
   * `X5 EDRIVE`; `model=X5` reaches only lots named exactly `X5`.
   *
   * FOUR CONDITIONS GUARD IT, and each is enforced here rather than trusted to
   * callers because every one of them produces a PLAUSIBLE PAGE rather than an
   * error — the failure mode nobody notices until a customer does.
   *
   *  1. Never alongside `model`. The exact filter wins and reverts the feature.
   *  2. Never containing whitespace. Measured, `q=X5 M` and `q=X5 MM` return
   *     HTTP 502 on three consecutive attempts while `q=X5 X` and
   *     `q=MX-5 MIATA` return 200 — so the failure is value-dependent and
   *     undocumented. We cannot predict which spaced values fail, so we send
   *     none.
   *  3. Never alongside `facility_id`. Measured: `facility_id=203&model=CIVIC`
   *     returns seven Civics, `facility_id=203&q=CIVIC` returns ZERO, under
   *     every combination tried. The Canadian per-yard path therefore keeps the
   *     exact fan-out.
   *  4. Never without `make`. A prefix is only accidentally unique — `q=X5`
   *     looks safe solely because BMW is currently the one make with a model
   *     starting "X5". The make filter makes a foreign car structurally
   *     impossible rather than currently unlikely.
   */
  const prefix = filters.modelPrefix?.trim();
  const canUsePrefix =
    Boolean(prefix) && !/\s/.test(prefix as string) && !facility && Boolean(filters.make);

  if (canUsePrefix) params.q = prefix;
  else if (filters.model) params.model = filters.model;

  if (filters.yearFrom) params.year_from = filters.yearFrom;
  if (filters.yearTo) params.year_to = filters.yearTo;

  /*
   * One house selected narrows the platform; both or neither leaves it open,
   * because "all" is the provider's default and naming both adds nothing.
   *
   * A named facility overrides that: its platform is not a preference but part
   * of the yard's identity, without which the id does not point anywhere in
   * particular.
   */
  if (facility) params.platform = facility.platform;
  else if (filters.houses.length === 1) params.platform = filters.houses[0];

  /*
   * Market. Canada is a fan-out over yards (see the note above); the USA is not
   * expressible at all, since there is no "exclude these yards" parameter and
   * enumerating ~400 US facilities one request at a time is absurd. A USA-only
   * selection is therefore narrowed AFTER normalisation, from the country each
   * lot resolves to. Honest, but it means a page can come back short.
   */
  if (facility) params.facility_id = facility.facilityId;

  /*
   * THE DEFAULT WINDOW STARTS TOMORROW, and this is the single most damaging
   * thing measured in this section.
   *
   * `auction_date_from` is DAY-granular, so a floor of today admits every sale
   * that already ran this morning — and the provider returns those FIRST.
   * Measured 2026-09-03 on the unfiltered feed: **160 consecutive lots, eight
   * full pages, every one `state: "finished"`**, with no open lot in reach. The
   * same shape per model: `make=BMW&model=X5` returned 20 rows of which 20 were
   * finished, so `/aukcje/bmw/x5` rendered 7 cars from trim spellings while the
   * vendor's own demo showed a full page of X5s.
   *
   * No amount of paging fixes a head that deep, and no upstream status filter
   * exists — `lot_sub_status=Open` is ACCEPTED AND IGNORED (a nonsense value
   * returns the identical page, the fourth time this API has done that). So the
   * floor moves to tomorrow, which excludes the finished head structurally.
   *
   * The cost is a sale still to come later today. That is a narrow slice against
   * a page that was otherwise empty, and it is the same trade `getSimilarLots`
   * makes for the same reason.
   *
   * A MULTI-DAY PRESET moves with it, and keeps its length: "Najbliższe 3 dni"
   * becomes tomorrow through day three. Measured: `today..+2` returned 20 rows
   * with 0 open, `tomorrow..+2` returned 20 with 20 open — the preset was as
   * empty as the default, for the same reason.
   *
   * "DZIŚ" IS THE ONE THAT CANNOT BE FIXED HERE. A floor of tomorrow with a
   * ceiling of today is an inverted range, so it keeps today — and today's page
   * is the finished head, 160 lots deep. That control cannot return anything
   * useful while the provider offers no status filter, which is a product
   * decision (drop it, or redefine it as "do 24 godzin"), not a query fix. The
   * top-up in `getAuctionLots` logs the shortfall rather than hiding it.
   */
  if (filters.mode === 'sold') {
    // Spec 015: the settled month behind us. A past window returns nothing
    // without `q`, so the root (no make, hence no model prefix) probes with
    // one broad letter; model pages already carry their prefix above.
    //
    // A MONTH, not the listed window. Spec 021 widened the listing to 180 days
    // in the store; this path cannot follow. Its `q=C` probe is the only way to
    // search the past here and it times out (502 after 27 s) beyond about 35
    // days, so asking for six months would turn the rollback path into an
    // outage. See SOLD_PROVIDER_WINDOW_START_DAYS_AGO.
    params.auction_date_from = auctionDay(-SOLD_PROVIDER_WINDOW_START_DAYS_AGO);
    params.auction_date_to = auctionDay(-SOLD_WINDOW_END_DAYS_AGO);

    if (!params.q && !params.model && !filters.make) params.q = SOLD_PROBE_PREFIX;
  } else if (filters.mode === 'now') {
    // Spec 015: auctions running at this moment. Today's window (the same
    // bounds the "Dziś" preset uses); the local rule below keeps the live ones.
    params.auction_date_from = auctionDay(0);
    params.auction_date_to = auctionDay(1);
  } else {
    const preset = filters.auctionWindow ? getAuctionDateWindow(filters.auctionWindow) : null;
    const isTodayOnly = preset?.days === 1;

    // Today by default (see `windowStart`); the day is granular, so today also
    // returns lanes that already ran, which the normaliser drops.
    params.auction_date_from = auctionDay(filters.windowStart === 'tomorrow' && !isTodayOnly ? 1 : 0);
    params.auction_date_to = auctionDay(preset ? preset.days : AUCTION_DATE_HORIZON_DAYS);
  }

  // Price maps to the current bid, the basis Gate 1 verified. The buy-now basis
  // is not offered here because filtering on it was never proven (FR-002).
  if (filters.priceFrom !== null) params.price_min = filters.priceFrom;
  if (filters.priceTo !== null) params.price_max = filters.priceTo;

  if (filters.odometerFrom !== null) params.odometer_from = filters.odometerFrom;
  if (filters.odometerTo !== null) params.odometer_to = filters.odometerTo;

  if (filters.transmission.length) params['transmission[]'] = filters.transmission;
  if (filters.fuel.length) params['fuel_type[]'] = filters.fuel;
  if (filters.color.length) params['color[]'] = filters.color;
  if (filters.damage.length) params['damage[]'] = filters.damage;
  if (filters.driveType.length) params['drive_type[]'] = filters.driveType;

  if (filters.runCondition) params.run_cond = filters.runCondition;
  if (filters.keys) params.has_key = filters.keys;

  /*
   * Buy-now, filtered UPSTREAM (spec 008).
   *
   * `lot_status` was marked dead by spec 007's Gate 1 — and that verdict was
   * wrong, because it was tested with a value I invented (`On Approval`). The
   * provider's own demo uses `Buy Now`, and with the real value the parameter
   * works exactly as documented: 20 of 20 returned rows carry a buy-now price,
   * against 0 of 20 unfiltered.
   *
   * This is the THIRD time a made-up value has produced a false "accepted and
   * ignored" verdict here, after `has_key=1` and `fuel_type=GAS`. A parameter
   * that returns the baseline unchanged means either the parameter is ignored or
   * the VALUE is — and those look identical from the outside. The vendor's
   * vocabulary is the only authority.
   */
  // Meaningless for a sale that has run (spec 015 FR-009).
  if (filters.buyNowOnly && filters.mode !== 'sold') params.lot_status = 'Buy Now';

  return params;
};

/**
 * Narrowing that the provider cannot do for us.
 *
 * USA-only is the real case: there is no way to exclude Canadian yards upstream,
 * so it is applied here and a page can come back short.
 *
 * Buy-now IS filtered upstream now via `lot_status`, and the check below is kept
 * anyway as a backstop. If that parameter ever silently stops filtering — the
 * failure mode this whole migration exists to prevent — the page comes back
 * short rather than filled with cars that have no buy-now price. Fewer cars is a
 * visible disappointment; wrong cars under a filter is a lie.
 */
export const applyLocalNarrowing = <
  T extends {
    country: 'USA' | 'CA';
    buyNowPrice: number | null;
    saleResult: { amount: number | null; status: string | null } | null;
    saleState: string | null;
    saleDate: Date | null;
  },
>(
  lots: T[],
  filters: TApibaraFilters
): T[] => {
  let result = lots;

  // Upcoming: a lot whose sale DAY has passed is over, whatever the provider
  // still calls it (it keeps ~7% of past lots "active"). The mirror ends such
  // lots by date (lifecycle pass); the provider path reads the same rule off
  // the date, so a listing never offers a lane that ran (2026-09-14).
  if (filters.mode === 'live') result = result.filter(lot => !isPast(lot.saleDate));

  // Sold mode keeps ONLY confirmed sales (spec 015 FR-006). "Sold on Approval"
  // is the house saying the seller may still decline, and a row with no status
  // did not sell — neither is a price anyone paid.
  if (filters.mode === 'sold') {
    result = result.filter(lot => lot.saleResult?.status === 'Sold');
  }

  // "Na żywo": only lots the house reports as being auctioned right now.
  if (filters.mode === 'now') result = result.filter(lot => lot.saleState === 'live');

  if (filters.markets.length === 1) {
    const wanted = filters.markets[0] === 'kanada' ? 'CA' : 'USA';

    result = result.filter(lot => lot.country === wanted);
  }

  /*
   * Buy-now-only, applied here rather than upstream.
   *
   * A buy-now price on the lot is direct evidence, needs no API call, and cannot
   * silently stop working.
   *
   * THIS IS LOAD-BEARING ON THE PREFIX PATH — do not delete it as redundant with
   * `lot_status` above. Measured 2026-09-03: `lot_status=Buy Now` filters
   * correctly with `model=` (20 of 20 returned rows carry a buy-now price) and
   * is SILENTLY IGNORED when `q` is present (12 of 20, identical to the
   * unfiltered baseline). That is the fifth "accepted and ignored" parameter on
   * this API and the first that is ignored only in the PRESENCE OF ANOTHER
   * PARAMETER — so it cannot be caught by testing the parameter on its own.
   *
   * Without this check a buy-now model page would show cars that cannot be
   * bought outright. With it the page may come back SHORT, which the top-up loop
   * absorbs and logs. Fewer cars is a visible disappointment; wrong cars under a
   * filter is a lie.
   */
  if (filters.buyNowOnly && filters.mode !== 'sold') result = result.filter(lot => lot.buyNowPrice !== null);

  return result;
};
